import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useLinkedEmployee } from "@/hooks/useLinkedEmployee";
import { getStudioSidebarTabs, isStudioNavPathActive } from "@/lib/studioNav";
import { isHqAdmin } from "@/lib/roleAccess";
import {
  lazyIntentHandlers,
  warmupAdminHome,
  warmupCreateUser,
  warmupEmployeeList,
  warmupEmployeeMaps,
  warmupEmployeePortal,
  warmupGenericOrgLanding,
  warmupLazyStudioHref,
  warmupMapsOverview,
  warmupManagerDashboard,
  warmupOrganizationDetail,
  warmupOrgadminDashboard,
  warmupOrgAssessmentEntry,
  warmupOrgEmployeeHome,
  warmupOrgStudio,
} from "@/routes/preloadChunks";

function studioSubNavLinkClass(active: boolean) {
  return (
    "flex min-h-[44px] items-center rounded-[var(--radius-md)] px-4 py-2 text-[13px] font-medium transition-colors duration-200 " +
    (active
      ? "border-l-2 border-l-[var(--color-highlight-blue)] bg-[var(--color-accent-muted)] text-[var(--color-highlight-blue)]"
      : "border-l-2 border-l-transparent text-[var(--color-steel-gray)] hover:bg-[var(--color-border-muted)]/60 hover:text-[var(--color-white)]")
  );
}

function EmployeeMyMapsNavLink({
  path,
  adminNavLinkClass,
}: {
  path: string;
  adminNavLinkClass: (active: boolean) => string;
}) {
  const { employeeId, loading } = useLinkedEmployee();
  if (loading || !employeeId) return null;
  const active = path.startsWith(`/employee/${employeeId}/`);
  return (
    <li>
      <Link
        to={`/employee/${employeeId}/maps`}
        className={adminNavLinkClass(active)}
        {...lazyIntentHandlers(warmupEmployeeMaps)}
      >
        Personal results
      </Link>
    </li>
  );
}

export function AppShellNav() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { user_type, isOrgConsoleContributor, isEmployee, isSuperadmin } = usePermissions();
  const path = location.pathname;
  const orgId = user?.org_id ?? null;

  const dashboardActive = path === "/admin" || path === "/admin/";

  const adminNavLinkClass = (active: boolean) =>
    "flex items-center min-h-[44px] px-4 py-2 rounded-[var(--radius-md)] text-sm font-medium transition-colors duration-150 " +
    (active
      ? "text-[var(--color-highlight-blue)] bg-[var(--color-accent-muted)] border-l-2 border-l-[var(--color-highlight-blue)]"
      : "text-[var(--color-steel-gray)] hover:bg-[var(--color-border-muted)] hover:text-[var(--color-white)]");

  if (user_type === "superadmin" || user_type === "admin") {
    const studioTabs = getStudioSidebarTabs(isEmployee, isSuperadmin);
    return (
      <ul className="space-y-1">
        <li>
          <Link to="/admin" {...lazyIntentHandlers(warmupAdminHome)} className={adminNavLinkClass(dashboardActive)}>
            Dashboard
          </Link>
        </li>
        {isHqAdmin(user) ? (
          <li className="space-y-1">
            <span className="flex min-h-[44px] cursor-default select-none items-center px-4 py-2 font-[var(--font-display)] text-sm font-semibold uppercase tracking-wider text-[var(--color-text-subtle)]">
              Studio
            </span>
            <ul className="ml-4 space-y-0.5 border-l border-white/10 pl-4" aria-label="Studio sections">
              {studioTabs.map((tab) => (
                <li key={tab.path}>
                  <Link
                    to={tab.path}
                    {...lazyIntentHandlers(() => warmupLazyStudioHref(tab.path))}
                    className={studioSubNavLinkClass(isStudioNavPathActive(path, tab.path))}
                  >
                    {tab.label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
        ) : null}
      </ul>
    );
  }

  if (user_type === "orgadmin") {
    const orgDetailsHref = orgId ? `/admin/organizations/${orgId}` : null;
    const orgDetailsActive =
      Boolean(orgId) && (path === `/admin/organizations/${orgId}` || path.startsWith(`/admin/organizations/${orgId}/`));
    const orgStudioActive =
      path === "/org/studio" ||
      path.startsWith("/org/studio/") ||
      path.startsWith("/studio/player/");

    return (
      <div className="flex h-full min-h-0 flex-col">
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          <li>
            <Link to="/org" {...lazyIntentHandlers(warmupOrgadminDashboard)} className={adminNavLinkClass(path === "/org" || path === "/org/")}>
              Dashboard
            </Link>
          </li>
          <li>
            <Link
              to="/org/employees"
              {...lazyIntentHandlers(warmupEmployeeList)}
              className={adminNavLinkClass(path.startsWith("/org/employees"))}
            >
              Employees
            </Link>
          </li>
          <li>
            <Link to="/org/studio" {...lazyIntentHandlers(warmupOrgStudio)} className={adminNavLinkClass(orgStudioActive)}>
              Studio
            </Link>
          </li>
          <li>
            <Link
              to="/org/users/new"
              {...lazyIntentHandlers(warmupCreateUser)}
              className={adminNavLinkClass(path.startsWith("/org/users/new"))}
            >
              Create user
            </Link>
          </li>
        </ul>
        {orgDetailsHref ? (
          <div className="shrink-0 border-t border-white/10 pt-3">
            <ul className="space-y-1">
              <li>
                <Link
                  to={orgDetailsHref}
                  {...lazyIntentHandlers(warmupOrganizationDetail)}
                  className={adminNavLinkClass(orgDetailsActive)}
                >
                  Organization Details
                </Link>
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  if (user_type === "orguser" && isOrgConsoleContributor) {
    const orgDetailsHref = orgId ? `/admin/organizations/${orgId}?tab=details` : null;
    const orgConsole = orgId ? `/admin/organizations/${orgId}?tab=curriculum` : null;
    const onOrgDetail =
      Boolean(orgId) && (path === `/admin/organizations/${orgId}` || path.startsWith(`/admin/organizations/${orgId}/`));
    const orgDetailTab = (searchParams.get("tab") ?? "").trim().toLowerCase();
    const curriculumActive = onOrgDetail && orgDetailTab === "curriculum";
    const orgDetailsActive = onOrgDetail && orgDetailTab === "details";

    return (
      <div className="flex h-full min-h-0 flex-col">
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto">
          <li>
            <Link
              to="/org"
              {...lazyIntentHandlers(warmupManagerDashboard)}
              className={adminNavLinkClass(path === "/org" || path === "/org/")}
            >
              Dashboard
            </Link>
          </li>
          {orgConsole ? (
            <li>
              <Link
                to={orgConsole}
                {...lazyIntentHandlers(warmupOrganizationDetail)}
                className={adminNavLinkClass(curriculumActive)}
              >
                Curriculum
              </Link>
            </li>
          ) : null}
          <li>
            <Link
              to="/org/studio"
              {...lazyIntentHandlers(warmupOrgStudio)}
              className={adminNavLinkClass(path === "/org/studio" || path.startsWith("/org/studio/"))}
            >
              Studio
            </Link>
          </li>
          <li>
            <Link
              to="/maps-overview"
              {...lazyIntentHandlers(warmupMapsOverview)}
              className={adminNavLinkClass(path.startsWith("/maps-overview"))}
            >
              Team &amp; MAPS
            </Link>
          </li>
          <li>
            <Link
              to="/org/employees"
              {...lazyIntentHandlers(warmupEmployeeList)}
              className={adminNavLinkClass(path.startsWith("/org/employees"))}
            >
              Team roster
            </Link>
          </li>
        </ul>
        {orgDetailsHref ? (
          <div className="shrink-0 border-t border-white/10 pt-3">
            <ul className="space-y-1">
              <li>
                <Link
                  to={orgDetailsHref}
                  {...lazyIntentHandlers(warmupOrganizationDetail)}
                  className={adminNavLinkClass(orgDetailsActive)}
                >
                  Organization Details
                </Link>
              </li>
            </ul>
          </div>
        ) : null}
      </div>
    );
  }

  if (user_type === "orguser" && isEmployee) {
    return (
      <ul className="space-y-1">
        <li>
          <Link to="/org" {...lazyIntentHandlers(warmupOrgEmployeeHome)} className={adminNavLinkClass(path === "/org" || path === "/org/")}>
            Home
          </Link>
        </li>
        <li>
          <Link
            to="/my"
            {...lazyIntentHandlers(warmupEmployeePortal)}
            className={adminNavLinkClass(path === "/my" || path.startsWith("/my/"))}
          >
            My program
          </Link>
        </li>
        <li>
          <Link
            to="/org/assessment"
            {...lazyIntentHandlers(warmupOrgAssessmentEntry)}
            className={adminNavLinkClass(path.startsWith("/org/assessment"))}
          >
            Take assessment
          </Link>
        </li>
        <EmployeeMyMapsNavLink path={path} adminNavLinkClass={adminNavLinkClass} />
      </ul>
    );
  }

  return (
    <ul className="space-y-1">
      <li>
        <Link to="/org" {...lazyIntentHandlers(warmupGenericOrgLanding)} className={adminNavLinkClass(path === "/org" || path === "/org/")}>
          Dashboard
        </Link>
      </li>
    </ul>
  );
}
